import { Editor } from "@tiptap/vue-3";
import { computed } from "vue";
import {
    faH1,
    faH2,
    faH3,
    faH4,
    faH5,
    faH6,
    faListOl,
    faListUl,
    faParagraph,
    faBold,
    faItalic,
    faUnderline,
    faStrikethrough,
    faAlignLeft,
    faAlignCenter,
    faAlignRight,
    faAlignJustify,
    faCode,
} from "@fortawesome/pro-regular-svg-icons";

export default function useEditorBase(editor: Ref<Editor>) {
    const styles = computed(() => {
        return [
            {
                active: editor.value.isActive("bold"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "bold",
                ),
                click: () => {
                    editor.value.chain().focus().toggleBold().run();
                },
                icon: faBold,
                label: "Bold",
            },
            {
                active: editor.value.isActive("italic"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "italic",
                ),
                click: () => editor.value.chain().focus().toggleItalic().run(),
                icon: faItalic,
                label: "Italic",
            },
            {
                active: editor.value.isActive("underline"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "underline",
                ),
                click: () =>
                    editor.value.chain().focus().toggleUnderline().run(),
                icon: faUnderline,
                label: "Underline",
            },
            {
                active: editor.value.isActive("strike"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "strike",
                ),
                click: () => editor.value.chain().focus().toggleStrike().run(),
                icon: faStrikethrough,
                label: "Strikethrough",
            },
        ].filter((btn) => btn.enabled);
    });

    const aligns = computed(() => {
        return editor.value?.extensionManager.extensions.find(
            (ext) => ext.name === "textAlign",
        )
            ? [
                  {
                      disabled: editor.value.isActive({ textAlign: "left" }),
                      click: () =>
                          editor.value
                              .chain()
                              .focus()
                              .setTextAlign("left")
                              .run(),
                      icon: faAlignLeft,
                      label: "Align: Left",
                  },
                  {
                      disabled: editor.value.isActive({ textAlign: "center" }),
                      click: () =>
                          editor.value
                              .chain()
                              .focus()
                              .setTextAlign("center")
                              .run(),
                      icon: faAlignCenter,
                      label: "Align: Center",
                  },
                  {
                      disabled: editor.value.isActive({ textAlign: "right" }),
                      click: () =>
                          editor.value
                              .chain()
                              .focus()
                              .setTextAlign("right")
                              .run(),
                      icon: faAlignRight,
                      label: "Align: Right",
                  },
                  {
                      disabled: editor.value.isActive({ textAlign: "justify" }),
                      click: () =>
                          editor.value
                              .chain()
                              .focus()
                              .setTextAlign("justify")
                              .run(),
                      icon: faAlignJustify,
                      label: "Align: Justify",
                  },
              ]
            : [];
    });

    const blocks = computed(() =>
        [
            {
                disabled: editor.value.isActive("paragraph"),
                click: () => editor.value.chain().focus().setParagraph().run(),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "paragraph",
                ),
                icon: faParagraph,
                label: "Paragraph",
            },
            ...[1, 2, 3, 4, 5, 6].map((level) => ({
                disabled: editor.value.isActive("heading", { level }),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "heading",
                ),
                click: () => {
                    editor.value.chain().focus().toggleHeading({ level }).run();
                },
                icon: {
                    1: faH1,
                    2: faH2,
                    3: faH3,
                    4: faH4,
                    5: faH5,
                    6: faH6,
                }[level],
                label: "Heading " + level,
            })),
            {
                disabled: editor.value.isActive("bulletList"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "bulletList",
                ),
                click: () =>
                    editor.value.chain().focus().toggleBulletList().run(),
                icon: faListUl,
                label: "Bullet List",
            },
            {
                disabled: editor.value.isActive("orderedList"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "orderedList",
                ),
                click: () =>
                    editor.value.chain().focus().toggleOrderedList().run(),
                icon: faListOl,
                label: "Ordered List",
            },
            {
                disabled: editor.value.isActive("codeBlock"),
                enabled: editor.value?.extensionManager.extensions.find(
                    (ext) => ext.name === "codeBlock",
                ),
                click: () =>
                    editor.value.chain().focus().toggleCodeBlock().run(),
                icon: faCode,
                label: "Code Block",
            },
        ].filter((btn) => btn.enabled),
    );

    const activeBlock = computed(() =>
        blocks.value.find((btn) => btn.disabled),
    );
    const activeAlign = computed(() =>
        aligns.value.find((btn) => btn.disabled),
    );

    return {
        styles,
        blocks,
        activeBlock,
        aligns,
        activeAlign,
    };
}
